import {Component, computed, effect, inject, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CreateTimesheetRequest, PartyType} from '../../../../../api';
import {CurrentUserService} from '../../../../services/current-user.service';
import {TimesheetStoreService} from '../../../../features/timesheets/services/timesheet-store.service';
import {ElciaDialog} from '../../../dialog/dialog';
import {ElciaIcon} from '../../../icon/icon';
import {TextInput} from '../../../text-input/text-input';
import {NumericInput} from '../../../numeric-input/numeric-input';
import {ToggleSwitch} from '../../../toggle-switch/toggle-switch';
import {PartyDropdown} from '../../../party-dropdown/party-dropdown';
import {TranslatePipe} from '@ngx-translate/core';

import {TextArea} from '../../../text-area/text-area';
import {
  TimesheetTypeDropdown
} from '../../../../features/timesheets/components/timesheet-type-dropdown/timesheet-type-dropdown';

@Component({
  selector: 'elcia-timesheet-timer',
  imports: [
    ElciaDialog,
    ElciaIcon,
    TextInput,
    NumericInput,
    ToggleSwitch,
    PartyDropdown,
    TranslatePipe,
    ReactiveFormsModule,
    TextArea,
    TimesheetTypeDropdown,
  ],
  templateUrl: './timesheet-timer.html',
  styleUrl: './timesheet-timer.scss',
})
export class TimesheetTimer {
  private readonly store = inject(TimesheetStoreService);
  private readonly currentUserService = inject(CurrentUserService);

  protected readonly isRunning = signal(false);
  protected readonly isDialogDisplayed = signal(false);
  protected readonly elapsedSeconds = signal(0);
  protected readonly isIdle = computed(() => !this.isRunning() && this.elapsedSeconds() === 0);
  protected readonly isStopped = computed(() => !this.isRunning() && this.elapsedSeconds() > 0);
  private startTime: Date | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  protected readonly formattedTime = computed(() => {
    const s = this.elapsedSeconds();
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  });

  protected readonly partyTypes = signal<PartyType[]>([PartyType.Company, PartyType.Contact]);

  protected readonly form = new FormGroup({
    subject: new FormControl<string | null>(null),
    date: new FormControl(new Date()),
    timesheetTypeId: new FormControl<number | null>(1, Validators.required),
    concernedPartyId: new FormControl<number | null>(null),
    isBillable: new FormControl(false, {nonNullable: true}),
    billableDurationInMinutes: new FormControl<number | null>(null),
    description: new FormControl<string | null>(null),
  });

  private readonly isBillable = toSignal(
    this.form.controls.isBillable.valueChanges,
    {initialValue: this.form.controls.isBillable.value},
  );

  // A billable timesheet must target a client (mirrors the backend rule).
  private readonly applyConcernedPartyValidator = effect(() => {
    const control = this.form.controls.concernedPartyId;
    if (this.isBillable()) {
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({emitEvent: false});
  });

  protected readonly isSaving = computed(() => this.store.createTimesheetMutation.isPending());

  protected readonly dialogActionLabel = computed(() => {
    if (this.isIdle()) return 'action.start';
    if (this.isRunning()) return 'action.save';
    return 'action.create';
  });

  protected readonly dialogActionIcon = computed(() => {
    if (this.isIdle()) return 'play';
    if (this.isRunning()) return 'save';
    return 'plus';
  });

  protected startTimer(): void {
    this.startTime = new Date();
    this.isRunning.set(true);
    this.intervalId = setInterval(() => {
      this.elapsedSeconds.update(s => s + 1);
    }, 1000);
  }

  protected stopTimer(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning.set(false);
    if (this.form.controls.billableDurationInMinutes.value === null) {
      this.form.controls.billableDurationInMinutes.setValue(Math.round(this.elapsedSeconds() / 60));
    }
  }

  protected resumeTimer(): void {
    this.isRunning.set(true);
    this.intervalId = setInterval(() => {
      this.elapsedSeconds.update(s => s + 1);
    }, 1000);
  }

  private resetTimer(): void {
    this.stopTimer();
    this.elapsedSeconds.set(0);
    this.startTime = null;
    this.form.reset();
    this.form.controls.date.setValue(new Date());
  }

  protected openDialog(): void {
    this.isDialogDisplayed.set(true);
  }

  protected closeDialog(): void {
    this.isDialogDisplayed.set(false);
  }

  protected async onDialogAction(): Promise<void> {
    if (this.isIdle()) {
      this.startTimer();
      return;
    }
    if (this.isRunning()) {
      this.closeDialog();
      return;
    }
    // isStopped → create
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const userId = this.currentUserService.currentUser()?.id;
    if (!userId || !this.startTime) return;

    const request: CreateTimesheetRequest = {
      subject: this.form.value.subject ?? null,
      concernedPartyId: this.form.value.concernedPartyId ?? null,
      startTime: this.startTime,
      date: this.form.value.date!,
      endTime: new Date(this.startTime.getTime() + this.elapsedSeconds() * 1000),
      description: this.form.value.description ?? null,
      timesheetTypeId: this.form.value.timesheetTypeId!,
      isBillable: this.form.value.isBillable!,
      billableDurationInSeconds: this.form.value.isBillable
        ? (this.form.value.billableDurationInMinutes ?? 0) * 60
        : 0,
      productIds: [],
      activityTypeIds: [],
    };

    await this.store.createTimesheetMutation.mutateAsync(request);
    this.resetTimer();
    this.closeDialog();
  }
}
